import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import {
  TDependencyOperators,
  TDependentFieldSpec,
  TDependentFieldsGroup,
  TFieldSpecification,
} from '@utils/form/hubspot/schema'

/** We use following markers in the HUBSPOT field to help us manage form fields on front-end
 * '[END]'       marker can be added to dependent field's label.
 *               If that's the case, we will put the field at the bottom of the form
 *               instead of putting it rght after its parent. formatFieldLabel function below help us clean it out before displaying it.
 *
 * '_option_[x]' marker should be added to dependent field's name if this field has different options.
 *               For example quote_foodsafe_quantity_option_1, quote_foodsafe_quantity_option_2, quote_foodsafe_quantity_option_3,
 *               Those fields will be represented as separate ContactProperties on the hubspot side,
 *               but no matter which one will be filled in the end. We will send the submitted value as {quote_foodsafe_quantity: value }
 *               That's what we're doing with formatFieldName function below.
 */
const FIELD_BOTTOM_POSITION_LABEL_MARKER = '[END]'
const FIELD_OPTION_NAME_MARKER = '_option_'

const formatFieldLabel = (label: string): string => {
  return label?.split(FIELD_BOTTOM_POSITION_LABEL_MARKER)?.[0]?.trim()
}

const formatFieldName = (fieldName: string): string => {
  return fieldName?.split(FIELD_OPTION_NAME_MARKER)?.[0]?.trim()
}

/**
 * Helper functions for the hubspot dependency conditons validation
 * */
const valueIsEqualToAnyListedOption = (
  value: string,
  allowedValues: string[]
) => {
  return allowedValues?.includes(value)
}

const valueIsNotEqualToListedOptions = (
  value: string,
  disallowedValues: string[]
) => {
  return !disallowedValues?.includes(value)
}
const valueDoesContain = (value: string, stringToFind: string) =>
  value.includes(stringToFind)

const strStartsWith = (value: string, stringToFind: string) =>
  value.indexOf(stringToFind) === 0

const strEndsWith = (value: string, stringToFind: string) =>
  value.indexOf(stringToFind) === value.length - stringToFind.length

export const dependencyConditionValidator: Record<
  TDependencyOperators,
  (enteredValue: string, conditionValue: string | string[]) => boolean
> = {
  is_not_empty: (enteredValue) => Boolean(enteredValue),
  set_any: valueIsEqualToAnyListedOption,
  set_not_any: valueIsNotEqualToListedOptions,
  eq: isEqual,
  neq: (enteredValue, conditionValue) => !isEqual(enteredValue, conditionValue),
  contains: valueDoesContain,
  doesnt_contain: (enteredValue, conditionValue) =>
    !valueDoesContain(enteredValue, conditionValue as string),
  str_starts_with: strStartsWith,
  str_ends_with: strEndsWith,
}

/**
 * Go through all the dependent fields of [parentField] and check if they pass the dependency condition to be disaplyed in a form
 * */
const findDependentFieldsToDisplay = ({
  parentField,
  parentFieldValue,
}: {
  parentField: TFieldSpecification
  parentFieldValue: string
}): TFieldSpecification[] => {
  const dependentFields: TDependentFieldSpec[] = parentField?.dependentFields

  if (isEmpty(dependentFields)) {
    return null
  }

  const fieldsToDisplay: TFieldSpecification[] = dependentFields
    ?.filter((x: any) => {
      const conditionOperator = x.dependentCondition?.operator
      const conditionValue: string | string[] =
        x.dependentCondition?.value || x.dependentCondition?.values

      if (
        dependencyConditionValidator[conditionOperator](
          parentFieldValue,
          conditionValue
        )
      ) {
        return x
      }

      return null
    })
    ?.filter(Boolean)
    ?.map((x) => x.dependentField)

  return fieldsToDisplay
}

const mergeDependentAndRegularFields = (
  dependentFieldsGroups: TDependentFieldsGroup[],
  formFields: TFieldSpecification[]
): TFieldSpecification[] => {
  const formFieldsCopy: TFieldSpecification[] = Array.from(formFields || [])

  for (const fieldsGroup of dependentFieldsGroups) {
    const parentFieldIndex = formFieldsCopy.findIndex(
      (x) => x.name === fieldsGroup?.parentFieldName
    )

    // to keep track how many field we inserted with .splice
    let insertedFields = 0

    for (const field of fieldsGroup?.fields) {
      if (field?.label?.includes(FIELD_BOTTOM_POSITION_LABEL_MARKER)) {
        // append field at the bottom of the form
        formFieldsCopy?.push(field)
      } else {
        // insert field right after parent
        formFieldsCopy.splice(parentFieldIndex + insertedFields + 1, 0, field)
        insertedFields++
      }
    }
  }

  return formFieldsCopy || []
}

export {
  formatFieldLabel,
  formatFieldName,
  findDependentFieldsToDisplay,
  mergeDependentAndRegularFields,
  FIELD_BOTTOM_POSITION_LABEL_MARKER,
}
